import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";

const S = {
  card:{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px",marginBottom:12},
  input:{width:"100%",padding:"7px 10px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  btn:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  th:{textAlign:"left",padding:"7px 12px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:11},
  td:{padding:"8px 12px",borderBottom:"0.5px solid #f5f5f0",fontSize:12,verticalAlign:"middle"},
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 7px",borderRadius:10,background:bg,color:c,fontWeight:500}),
};

function fileIcon(name){
  const ext=(name||"").split(".").pop().toLowerCase();
  if(ext==="pdf") return "📄";
  if(["xlsx","xls","csv"].includes(ext)) return "📊";
  if(["doc","docx"].includes(ext)) return "📝";
  if(["jpg","jpeg","png","gif","webp"].includes(ext)) return "🖼";
  return "📎";
}

export default function AdvancedSearch({ initialQuery="", isAdmin, clientName }){
  const [keyword, setKeyword] = useState(initialQuery);
  const [type, setType] = useState("all");
  const [clientFilter, setClientFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [files, setFiles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [searched, setSearched] = useState(!!initialQuery);

  useEffect(()=>{
    const unsubs = [];
    unsubs.push(onSnapshot(collection(db,"files"), snap=>setFiles(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"file"})))));
    unsubs.push(onSnapshot(collection(db,"tasks"), snap=>setTasks(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"task"})))));
    if(isAdmin){
      unsubs.push(onSnapshot(collection(db,"clients"), snap=>setClients(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"client"})))));
      unsubs.push(onSnapshot(collection(db,"invoices"), snap=>setInvoices(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"invoice"})))));
    }
    return ()=>unsubs.forEach(u=>u());
  },[isAdmin]);

  const clientNames = [...new Set(files.map(f=>f.client).filter(Boolean))].sort();

  const results = useMemo(()=>{
    if(!searched) return [];
    const q = keyword.toLowerCase().trim();
    const myClient = !isAdmin ? clientName : clientFilter;
    let out = [];

    const matchDate = (dateStr) => {
      if(!dateStr) return true;
      const d = new Date(dateStr);
      if(dateFrom && d < new Date(dateFrom)) return false;
      if(dateTo && d > new Date(dateTo+"T23:59:59")) return false;
      return true;
    };

    if(type==="all"||type==="file"){
      files.filter(f=>{
        if(myClient && f.client!==myClient) return false;
        if(!matchDate(f.uploadedAt)) return false;
        if(!q) return true;
        return (f.name||"").toLowerCase().includes(q)||(f.client||"").toLowerCase().includes(q)||(f.folderName||"").toLowerCase().includes(q)||(f.year||"").includes(q);
      }).forEach(f=>out.push({...f,_sortDate:f.uploadedAt||""}));
    }

    if(type==="all"||type==="task"){
      tasks.filter(t=>{
        if(myClient && t.client!==myClient) return false;
        if(!matchDate(t.createdAt)) return false;
        if(!q) return true;
        return (t.title||"").toLowerCase().includes(q)||(t.client||"").toLowerCase().includes(q)||(t.notes||"").toLowerCase().includes(q)||(t.category||"").toLowerCase().includes(q);
      }).forEach(t=>out.push({...t,_sortDate:t.createdAt||""}));
    }

    if(isAdmin&&(type==="all"||type==="client")){
      clients.filter(c=>{
        if(!q) return false;
        return (c.name||"").toLowerCase().includes(q)||(c.email||"").toLowerCase().includes(q)||(c.contact||"").toLowerCase().includes(q);
      }).forEach(c=>out.push({...c,_sortDate:c.createdAt||""}));
    }

    if(isAdmin&&(type==="all"||type==="invoice")){
      invoices.filter(i=>{
        if(myClient && i.client!==myClient) return false;
        if(!matchDate(i.createdAt)) return false;
        if(!q) return true;
        return (i.client||"").toLowerCase().includes(q)||(i.service||"").toLowerCase().includes(q)||(i.amount||"").toLowerCase().includes(q);
      }).forEach(i=>out.push({...i,_sortDate:i.createdAt||""}));
    }

    // Sort
    out.sort((a,b)=>{
      let av,bv;
      if(sortBy==="date"){ av=a._sortDate||""; bv=b._sortDate||""; }
      else if(sortBy==="name"){ av=(a.name||a.title||a.client||"").toLowerCase(); bv=(b.name||b.title||b.client||"").toLowerCase(); }
      else if(sortBy==="client"){ av=(a.client||"").toLowerCase(); bv=(b.client||"").toLowerCase(); }
      else if(sortBy==="type"){ av=a._type; bv=b._type; }
      if(av<bv) return sortDir==="asc"?-1:1;
      if(av>bv) return sortDir==="asc"?1:-1;
      return 0;
    });

    return out;
  },[searched, keyword, type, clientFilter, dateFrom, dateTo, sortBy, sortDir, files, tasks, clients, invoices, isAdmin, clientName]);

  const typeColors = {file:["#E6F1FB","#185FA5"],task:["#EAF3DE","#3B6D11"],client:["#EEEDFE","#534AB7"],invoice:["#FAEEDA","#854F0B"]};

  return (
    <div>
      <div style={{fontSize:14,fontWeight:500,marginBottom:14}}>🔎 Advanced Search</div>

      {/* Filters */}
      <div style={S.card}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Search keyword</div>
            <input style={S.input} value={keyword} onChange={e=>setKeyword(e.target.value)}
              placeholder="File name, client, task title..."
              onKeyDown={e=>e.key==="Enter"&&setSearched(true)}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Type</div>
            <select style={S.input} value={type} onChange={e=>setType(e.target.value)}>
              <option value="all">All types</option>
              <option value="file">Files only</option>
              <option value="task">Tasks only</option>
              {isAdmin&&<option value="client">Clients only</option>}
              {isAdmin&&<option value="invoice">Invoices only</option>}
            </select>
          </div>
          {isAdmin&&(
            <div>
              <div style={{fontSize:11,color:"#888",marginBottom:3}}>Client</div>
              <select style={S.input} value={clientFilter} onChange={e=>setClientFilter(e.target.value)}>
                <option value="">All clients</option>
                {clientNames.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          )}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:12}}>
          <div>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Date from</div>
            <input style={S.input} type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Date to</div>
            <input style={S.input} type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Sort by</div>
            <select style={S.input} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
              <option value="date">Date</option>
              <option value="name">Name</option>
              <option value="client">Client</option>
              <option value="type">Type</option>
            </select>
          </div>
          <div>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Order</div>
            <select style={S.input} value={sortDir} onChange={e=>setSortDir(e.target.value)}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={S.btnPrimary} onClick={()=>setSearched(true)}>Search</button>
          <button style={S.btn} onClick={()=>{setKeyword("");setType("all");setClientFilter("");setDateFrom("");setDateTo("");setSearched(false);}}>Clear</button>
          {searched&&<span style={{fontSize:12,color:"#888",alignSelf:"center"}}>{results.length} result{results.length!==1?"s":""}</span>}
        </div>
      </div>

      {/* Results */}
      {searched&&(
        <div style={S.card}>
          {results.length===0?(
            <div style={{textAlign:"center",padding:"32px 0",color:"#aaa",fontSize:13}}>
              No results found. Try different keywords or filters.
            </div>
          ):(
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr>
                <th style={S.th}>Type</th>
                <th style={S.th}>Name / Title</th>
                <th style={S.th}>Client</th>
                <th style={S.th}>Details</th>
                <th style={S.th}>Date</th>
                <th style={S.th}></th>
              </tr></thead>
              <tbody>
                {results.map((r,i)=>(
                  <tr key={r.firestoreId||i}>
                    <td style={S.td}><span style={S.badge(...(typeColors[r._type]||["#f5f5f0","#555"]))}>{r._type}</span></td>
                    <td style={S.td}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:16}}>{r._type==="file"?fileIcon(r.name):r._type==="task"?"✅":r._type==="client"?"👤":"💳"}</span>
                        <div style={{fontWeight:500,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}}>
                          {r._type==="file"?r.name:r._type==="task"?r.title:r._type==="client"?r.name:`${r.num||""} ${r.service||""}`}
                        </div>
                      </div>
                    </td>
                    <td style={{...S.td,color:"#888"}}>{r.client||"—"}</td>
                    <td style={{...S.td,color:"#888",fontSize:11}}>
                      {r._type==="file"&&`${r.folderName||""} · ${r.year||""} · ${r.size||""}`}
                      {r._type==="task"&&`${r.category||""} · ${r.status||""}`}
                      {r._type==="client"&&`${r.email||""} · ${r.cityState||""}`}
                      {r._type==="invoice"&&`${r.amount||""} · ${r.status||""}`}
                    </td>
                    <td style={{...S.td,color:"#aaa",fontSize:11,whiteSpace:"nowrap"}}>
                      {r._sortDate?new Date(r._sortDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):"—"}
                    </td>
                    <td style={S.td}>
                      {r._type==="file"&&r.url&&(
                        <a href={r.url} target="_blank" rel="noreferrer" style={{fontSize:11,color:"#185FA5",textDecoration:"none"}}>↓ Open</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
